import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { MaskLineScroll } from "../components/Reveal";
import { BRAND } from "../lib/brand";

const STEPS = [
  { n: "01", t: "Send us the claim details", d: "Insurance company, claim number, adjuster contact — plus a description of the damage." },
  { n: "02", t: "Assessment & documentation", d: "In-person assessment, photo documentation, and repair-plan development." },
  { n: "03", t: "Coordination with the adjuster", d: "We communicate directly with your insurance company to reduce your back-and-forth." },
  { n: "04", t: "Repair, refinish, inspect", d: "The work is completed to spec, inspected, and prepared for release." },
];

export default function Insurance() {
  return (
    <>
      <SEO
        title="Insurance Collision Repair — ProForm Denver"
        description="ProForm handles insurance claim workflows for daily-driven vehicles in Denver, Colorado. Clear communication with your adjuster, disciplined repair, honest inspection."
        canonical={`https://${BRAND.domain}/insurance`}
      />

      <section className="pt-32 md:pt-40 pb-16 border-b border-ink-700">
        <div className="wrap grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <div className="label mb-3">— Insurance · 001</div>
            <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight">
              <MaskLineScroll>Insurance claims,</MaskLineScroll>
              <MaskLineScroll delay={0.08}>handled <span className="text-proform-red">correctly</span>.</MaskLineScroll>
            </h1>
            <p className="mt-6 text-chrome-300 text-lg leading-relaxed max-w-xl">
              You aren&apos;t obligated to use your insurance company&apos;s preferred shop. ProForm works with major carriers and coordinates directly with your adjuster.
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="card p-6 md:p-8">
              <div className="label mb-3">Bring with you</div>
              <ul className="text-chrome-100 space-y-2 text-sm">
                <li>· Insurance company &amp; claim number</li>
                <li>· Adjuster name &amp; phone / email</li>
                <li>· Any photos taken at the scene</li>
                <li>· Copy of the police report (if applicable)</li>
                <li>· Your preferred contact method</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-ink-900 border-b border-ink-700">
        <div className="wrap">
          <div className="label mb-3">— Workflow</div>
          <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight mb-12 max-w-4xl">
            <MaskLineScroll>Four steps.</MaskLineScroll>
            <MaskLineScroll delay={0.08}>No <span className="text-proform-red">runaround</span>.</MaskLineScroll>
          </h2>
          <ol className="grid md:grid-cols-4 gap-4 md:gap-6">
            {STEPS.map((s) => (
              <li key={s.n} className="card p-6 md:p-8">
                <div className="font-display text-6xl text-proform-red leading-none">{s.n}</div>
                <div className="mt-6 font-display uppercase text-xl">{s.t}</div>
                <p className="mt-3 text-chrome-300 text-sm">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-20 md:py-28 border-b border-ink-700">
        <div className="wrap grid md:grid-cols-2 gap-10">
          {[
            { t: "You have the right to choose", d: "Colorado drivers can choose any qualified body shop for insurance repairs. Your carrier can recommend — they can't require." },
            { t: "Documentation matters", d: "Every panel, every process, every part decision is documented — for the adjuster, and for you." },
            { t: "Honest scoping", d: "If we can't do a job correctly, we say so up-front. We'd rather refer you to a specialist than start work that will fail." },
            { t: "Post-repair inspection", d: "Nothing leaves without a walk-around. Panel gaps, paint match, and final assembly are reviewed with you." },
          ].map((c) => (
            <div key={c.t} className="card p-8">
              <div className="font-display uppercase text-2xl md:text-3xl">{c.t}</div>
              <p className="mt-3 text-chrome-300">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 text-center">
        <div className="wrap max-w-3xl mx-auto">
          <h3 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight">
            <MaskLineScroll>Send the</MaskLineScroll>
            <MaskLineScroll delay={0.08}>claim <span className="text-proform-red">details</span>.</MaskLineScroll>
          </h3>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/estimate" className="btn btn-primary">Start an intake →</Link>
            <a href={`tel:${BRAND.phoneRaw}`} className="btn">Call {BRAND.phoneDisplay}</a>
          </div>
        </div>
      </section>
    </>
  );
}
